import React, { useEffect, useState } from "react";

import "./UninvoicedItems.scss";
import {
  Checkbox,
  FormControl,
  MenuItem,
  Select,
  TablePagination
} from "@material-ui/core";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Button from "@material-ui/core/Button";
import StorageIcon from "@material-ui/icons/Storage";
import InfoIcon from "@material-ui/icons/Info";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ImportExportIcon from "@material-ui/icons/ImportExport";
import { MoneyCorpPermissions } from "../../hooks/usePermissions/usePermissions";

const useStyles = makeStyles({
  formControl: {
    width: "20vw"
  },
  dropDownField: {
    fontSize: "0.9rem",
    marginLeft: "10px",
    width: "100%"
  },
  allBranches: {
    fontSize: "0.9rem",
    marginLeft: "10px",
    width: "100%",
    borderBottom: "1px solid lightgray"
  },
  hideRow: {
    display: "none"
  }
});

const UninvoicedItems = (props: {
  permissions: MoneyCorpPermissions;
  items?: any[];
  paginationInfo?: any;
  onChangePageNumber?: any;
  onChangePageSize?: any;
  onCreateInvoice?: (v: number[]) => void;
  onSort?: (x: any) => void;
}) => {
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [branchName, setBranchName] = useState<string | null>("All branches");
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [page, setPage] = useState(props.paginationInfo.currentPage - 1);

  const typeSortIndex = 0;
  const branchSortIndex = 1;
  const currencySortIndex = 2;

  const [sortCriteria, setSortCriteria] = useState<any>({
    sortBy: null,
    sortDirection: null
  });
  const [sortClickCount, setSortClickCount] = useState(0);

  const classes = useStyles();

  useEffect(() => {
    setSelectedOrderIds([]);
  }, [branchName]);

  function handleNewInvoiceButtonClick() {
    if (props.onCreateInvoice) {
      props.onCreateInvoice(selectedOrderIds);
      setSelectedOrderIds([]);
    }
  }

  function getBranchName(orderId: number): string {
    const order = props.items && props.items.find((o: any) => o.id === orderId);
    if (order) {
      return order.branch.name;
    }
    return "";
  }

  function getType(orderId: number): string {
    const order = props.items && props.items.find((o: any) => o.id === orderId);
    if (order) {
      return order.type;
    }
    return "";
  }

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    props.onChangePageNumber(newPage + 1);
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPage(0);
    props.onChangePageNumber(1);
    props.onChangePageSize(parseInt(event.target.value, 10));
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  function handleSorting(headerName: number) {
    if (sortCriteria.sortBy === headerName) {
      if (sortClickCount % 2 === 0) {
        setSortCriteria({ sortBy: headerName, sortDirection: 1 });
        props.onSort && props.onSort({ sortBy: headerName, sortDirection: 1 });
      } else {
        setSortCriteria({ sortBy: headerName, sortDirection: 0 });
        props.onSort && props.onSort({ sortBy: headerName, sortDirection: 0 });
      }
      setSortClickCount(n => n + 1);
    } else if (sortCriteria.sortBy !== headerName) {
      setSortCriteria({ sortBy: headerName, sortDirection: 0 });
      props.onSort && props.onSort({ sortBy: headerName, sortDirection: 0 });
      setSortClickCount(0);
    }
  }

  return (
    <div className="UninvoicedItems--container">
      <div className="OrdersReturnsTable--headersContainer">
        {branchName === "All branches" ? (
          <div className="OrdersReturnsTable--sameBranchHeader">
            <InfoIcon className="OrdersReturnsTable--infoIcon"></InfoIcon>
            <div>Only orders from the same branch may be selected.</div>
          </div>
        ) : (
          <></>
        )}
        <div className="OrdersReturnsTable--sameBranchHeader">
          <InfoIcon className="OrdersReturnsTable--infoIcon"></InfoIcon>
          <div>Only the same type of item may be selected.</div>
        </div>
      </div>
      <div className="OrdersReturnsTable--tablePanel">
        <div className="OrdersReturnsTable--header">Orders/Returns</div>
        <div className="OrdersReturnsTable--dropDownContainer">
          <div className="OrdersReturnsTable--dropDownHeader">Branch</div>
          <div className="OrdersReturnsTable--dropDown">
            <FormControl className={classes.formControl}>
              <Select
                className={classes.dropDownField}
                value={branchName}
                variant="outlined"
                onChange={(evt: any) =>
                  setBranchName(evt.target.value as string)
                }
                displayEmpty
                renderValue={(branchName: any) => {
                  if (branchName === null) {
                    return (
                      <MenuItem
                        className={classes.dropDownField}
                        disabled
                        key=""
                        value=""
                      >
                        Select a branch
                      </MenuItem>
                    );
                  } else {
                    return (
                      <MenuItem
                        className={classes.dropDownField}
                        key=""
                        value=""
                      >
                        {branchName}
                      </MenuItem>
                    );
                  }
                }}
              >
                <MenuItem
                  className={classes.allBranches}
                  key=""
                  value="All branches"
                >
                  Show all branches
                </MenuItem>
                {props.items &&
                  props.items
                    .filter(
                      (item, i, arr) =>
                        arr.findIndex(
                          t => t.branch.name === item.branch.name
                        ) === i
                    )
                    .map((item: any, i: number) => (
                      <MenuItem
                        className={classes.dropDownField}
                        key={i}
                        value={item.branch.name}
                      >
                        {item.branch.name}
                      </MenuItem>
                    ))}
              </Select>
            </FormControl>
          </div>
        </div>
      </div>
      <div className="InvoicesList--pagination-container">
        <TablePagination
          component="div"
          count={(props.paginationInfo && props.paginationInfo.totalCount) || 0}
          page={page}
          onChangePage={handleChangePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100, 200]}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </div>
      <table className="OrdersReturnsTable--container">
        <thead className="OrdersReturnsTable--headPanel">
          <tr className="OrdersReturnsTable--headPanelRow">
            <th>Include</th>
            <th>Number</th>
            <th
              onClick={() => handleSorting(typeSortIndex)}
              className="OrdersReturnsTable--sortHeader"
            >
              <div className="OrdersReturnsTable--tableHeader">
                <div>Type</div>
                {sortCriteria.sortBy === typeSortIndex &&
                sortCriteria.sortDirection === 0 ? (
                  <ArrowUpwardIcon
                    className={"OrdersReturnsTable--ascendingIcon"}
                  />
                ) : sortCriteria.sortBy === typeSortIndex &&
                  sortCriteria.sortDirection === 1 ? (
                  <ArrowDownwardIcon
                    className={"OrdersReturnsTable--descendingIcon"}
                  />
                ) : (
                  <ImportExportIcon
                    className={"OrdersReturnsTable--unsortedIcon"}
                  />
                )}
              </div>
            </th>
            <th
              onClick={() => handleSorting(branchSortIndex)}
              className="OrdersReturnsTable--sortHeader"
            >
              <div className="OrdersReturnsTable--tableHeader">
                <div>Branch Name</div>
                {sortCriteria.sortBy === branchSortIndex &&
                sortCriteria.sortDirection === 0 ? (
                  <ArrowUpwardIcon
                    className={"OrdersReturnsTable--ascendingIcon"}
                  />
                ) : sortCriteria.sortBy === branchSortIndex &&
                  sortCriteria.sortDirection === 1 ? (
                  <ArrowDownwardIcon
                    className={"OrdersReturnsTable--descendingIcon"}
                  />
                ) : (
                  <ImportExportIcon
                    className={"OrdersReturnsTable--unsortedIcon"}
                  />
                )}
              </div>
            </th>
            <th
              onClick={() => handleSorting(currencySortIndex)}
              className="OrdersReturnsTable--sortHeader"
            >
              <div className="OrdersReturnsTable--tableHeader">
                <div>Currency</div>
                {sortCriteria.sortBy === currencySortIndex &&
                sortCriteria.sortDirection === 0 ? (
                  <ArrowUpwardIcon
                    className={"OrdersReturnsTable--ascendingIcon"}
                  />
                ) : sortCriteria.sortBy === currencySortIndex &&
                  sortCriteria.sortDirection === 1 ? (
                  <ArrowDownwardIcon
                    className={"OrdersReturnsTable--descendingIcon"}
                  />
                ) : (
                  <ImportExportIcon
                    className={"OrdersReturnsTable--unsortedIcon"}
                  />
                )}
              </div>
            </th>
            <th>Currency Amount</th>
            <th>Comments</th>
            <th>Delivery Address</th>
            <th>Settlement</th>
            <th>Date Placed</th>
            <th>Date Required</th>
          </tr>
        </thead>
        <tbody>
          {props.items &&
            props.items
              .filter((item: any) => {
                if (branchName === "All branches") {
                  return item;
                } else {
                  return item.branch.name === branchName;
                }
              })
              .map(i => (
                <tr className={"OrdersReturnsTable--row"} key={i.id}>
                  <td className="OrdersReturnsTable-include">
                    <Checkbox
                      disabled={
                        (selectedOrderIds.length > 0 &&
                          i.branch.name !==
                            getBranchName(selectedOrderIds[0])) ||
                        (selectedOrderIds.length > 0 &&
                          getType(selectedOrderIds[0]) !== i.type)
                      }
                      checked={selectedOrderIds.includes(i.id)}
                      onChange={event => {
                        if (event.target.checked) {
                          setSelectedOrderIds((ids: number[]) => [
                            ...ids,
                            i.id
                          ]);
                        } else {
                          setSelectedOrderIds((ids: number[]) => {
                            const result = [...ids];
                            if (result.indexOf(i.id) !== -1) {
                              result.splice(result.indexOf(i.id), 1);
                            }
                            return result;
                          });
                        }
                      }}
                      inputProps={{ "aria-label": "primary checkbox" }}
                    />
                  </td>
                  <td className="OrdersReturnsTable-number">{i.id}</td>
                  <td className="OrdersReturnsTable-type">{i.type}</td>
                  <td className="OrdersReturnsTable-branchName">
                    {i.branch.name}
                  </td>
                  <td className="OrdersReturnsTable-currency">
                    {i.currencyRegion} - {i.currencyName}
                  </td>
                  <td className="OrdersReturnsTable-currencyAmount">
                    {i.currencyAmount.toLocaleString(navigator.language, {
                      minimumFractionDigits: 2
                    })}
                  </td>
                  <td className="OrdersReturnsTable-comments">{i.comments}</td>
                  <td className="OrdersReturnsTable-deliveryAddress">
                    <div>{i.branch.streetAddress}</div>
                    <div>{i.branch.city}</div>
                    <div>{i.branch.county}</div>
                    <div>{i.branch.postCode}</div>
                  </td>
                  <td className="OrdersReturnsTable-settlement">GBP</td>
                  <td className="OrdersReturnsTable-dateOrdered">
                    {i.dateOrdered}
                  </td>
                  <td className="OrdersReturnsTable-dateRequired">
                    {i.dateRequired}
                  </td>
                </tr>
              ))}
        </tbody>
      </table>

      {props.items && props.items.length === 0 ? (
        <div className="OrdersReturnsTable--noDataContainer">
          <StorageIcon className="OrdersReturnsTable--noDataIcon" />
          <p className="OrdersReturnsTable--noDataText">
            There are currently no uninvoiced items to display.
          </p>
        </div>
      ) : (
        <></>
      )}

      {props.permissions && props.permissions.invoicing && (
        <div className="UninvoicedItems--buttonContainer">
          <Button
            className="UninvoicedItems-newInvoiceButton"
            variant="contained"
            onClick={handleNewInvoiceButtonClick}
            color="primary"
            disabled={selectedOrderIds.length === 0}
          >
            {" "}
            New Invoice / Credit Note
          </Button>
        </div>
      )}
    </div>
  );
};

export default UninvoicedItems;
